import React from 'react';
import { Modal, TextArea } from '@gerdau/hefesto';
import { Chips } from '@gerdau/hefesto-mais-web';

import { useDispatch } from 'react-redux';

import {
  ChipsContainer,
  ChipsDescription,
  ChipsWrapper,
  Container,
  ModalContainer,
  ModalSubtitle,
  SurveyContainer,
  SurveySuccess,
  SurveyTitle,
  SurveyHeader
} from './styles';
import {
  EResearchNps,
  ISaveUser,
  IUserState
} from '../../../../../store/ducks/user/types';
import { sendTagging } from '../../../../../store/ducks/tagging/actions';
import { saveUser } from '../../../../../store/ducks/user/actions';
import { UserService } from '../../../../../services/User';
import { addToast } from '../../../../../store/ducks/toast/actions';
import { EToastTypes } from '../../../../../store/ducks/toast/types';
import { purchaseOrigin } from '../../../../../services/MVP/utils';
import { getTaggingInformationScreen } from '../../../../../utils/Tagging';
import { useLocation } from 'react-router-dom';
import { GetFlag } from '../../../../../utils/featureFlag';

interface JourneySurveyProps {
  user: IUserState;
}

const dictionaryPurchase = {
  vitrine: 'newJorneySurveyShowcase',
  comprar_selecionando_itens: 'newJorneySurveyItems',
  planilha: 'newJorneySurveySheet',
  historico: 'newJorneySurveyHistory'
};

export type SurveyJorneyProps =
  | 'newJorneySurveyShowcase'
  | 'newJorneySurveyItems'
  | 'newJorneySurveySheet'
  | 'newJorneySurveyHistory';

export type SurveyTypeProps =
  | 'catalog'
  | 'search-items'
  | 'spreadsheet-upload'
  | 'repeat-order';

export type TipoCompraProps =
  | 'vitrine'
  | 'comprar_selecionando_itens'
  | 'planilha'
  | 'historico';

const dictionary = {
  vitrine: 'catalog',
  comprar_selecionando_itens: 'search-items',
  planilha: 'spreadsheet-upload',
  historico: 'repeat-order'
};

export const JourneySurvey: React.FC<JourneySurveyProps> = ({ user }) => {
  const flagPurchaseJourney = GetFlag('release-toggle-enable-jorney-survey');
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { commerceScreenName } = getTaggingInformationScreen(pathname);

  const tipo_compra = purchaseOrigin() as TipoCompraProps;
  const purchaseType = dictionary[tipo_compra] as SurveyTypeProps;
  const [rating, setRating] = React.useState<string[]>();
  const [npsText, setNpsText] = React.useState<string | undefined>(undefined);
  const [surveySent, setSurveySent] = React.useState(false);
  const [showSurveyModal, setShowSurveyModal] = React.useState(false);
  const [answered, setAnswered] = React.useState<string[]>([]);

  const [loading, setLoading] = React.useState(false);

  const disabled = loading || !rating || rating.length <= 0;

  const plainOptions = ['1', '2', '3', '4', '5'].map((opt) => ({
    id: opt,
    label: opt
  }));

  const userSurveys = user?.userSurveys;
  const survey = dictionaryPurchase[tipo_compra] as SurveyJorneyProps;
  const checkSurvey = userSurveys && userSurveys[survey];

  React.useEffect(() => {
    if (
      flagPurchaseJourney &&
      tipo_compra &&
      pathname.includes('/long-steel/commerce/finalize-order') &&
      !answered.includes(survey) &&
      (!checkSurvey ||
        checkSurvey?.status === EResearchNps.PENDING ||
        !checkSurvey?.status)
    ) {
      dispatch(
        sendTagging({
          screenName: 'pedido_finalizado',
          modal_name: 'pesquisa_de_satisfacao_gerdau_mais',
          event: 'feedback_modal_aberto',
          tipo_compra
        })
      );
      setRating(['']);
      setNpsText(undefined);
      setSurveySent(false);
      return setShowSurveyModal(true);
    }
  }, [flagPurchaseJourney, pathname]);

  const updateUser = ({
    surveyType,
    surveyStatus
  }: {
    surveyType: SurveyTypeProps;
    surveyStatus: EResearchNps;
  }) => {
    const saveUserProps: ISaveUser = {
      user,
      ...(surveyType === 'catalog' && {
        jorneySurveyShowcaseStatus: surveyStatus
      }),
      ...(surveyType === 'repeat-order' && {
        jorneySurveyHistoryStatus: surveyStatus
      }),
      ...(surveyType === 'search-items' && {
        jorneySurveyItemsStatus: surveyStatus
      }),
      ...(surveyType === 'spreadsheet-upload' && {
        jorneySurveySheetStatus: surveyStatus
      })
    };
    dispatch(saveUser(saveUserProps));
  };

  const onAnswerLaterClick = () => {
    dispatch(
      sendTagging({
        screenName: 'pedido_finalizado',
        modal_name: 'pesquisa_de_satisfacao_gerdau_mais',
        event: 'feedback_avaliar_mais_tarde',
        tipo_compra
      })
    );
    updateUser({
      surveyType: purchaseType,
      surveyStatus: EResearchNps.PENDING
    });
    setShowSurveyModal(false);
  };

  const onDontWantAnswerClick = () => {
    updateUser({
      surveyType: purchaseType,
      surveyStatus: EResearchNps.DECLINED
    });
    setAnswered((prev) => [...prev, survey]);
    setShowSurveyModal(false);
  };

  const onSendClick = () => {
    if (!rating || rating.length <= 0) {
      return;
    }
    setLoading(true);

    dispatch(
      sendTagging({
        screenName: 'pedido_finalizado',
        modal_name: 'pesquisa_de_satisfacao_gerdau_mais',
        event: 'feedback_enviar',
        tipo_compra
      })
    );

    if (npsText) {
      dispatch(
        sendTagging({
          screenName: 'pedido_finalizado',
          modal_name: 'pesquisa_de_satisfacao_gerdau_mais',
          event: 'feedback_motivo_nota',
          motivo: npsText,
          tipo_compra
        })
      );
    }

    if (purchaseType === 'catalog') {
      UserService.sendSurveyShowCase({
        status: EResearchNps.ANSWERED,
        rating: rating[0],
        surveyText: npsText
      })
        .then(() => {
          setSurveySent(true);
          dispatch(
            sendTagging({
              screenName: 'pedido_finalizado',
              modal_name: 'pesquisa_de_satisfacao_gerdau_mais',
              event: 'feedback_modal_enviado',
              tipo_compra
            })
          );
        })
        .catch((err) => {
          console.error({ err });
          setLoading(false);
          dispatch(
            sendTagging({
              event: 'error',
              screenName: commerceScreenName,
              mensagem:
                'Falha ao enviar a resposta. Por favor, tente novamente.'
            })
          );
          dispatch(
            addToast({
              type: EToastTypes.DANGER,
              message:
                'Falha ao enviar a resposta. Por favor, tente novamente.',
              durationInMS: 5000
            })
          );
        })
        .finally(() => setLoading(false));
    }
    if (purchaseType === 'search-items') {
      UserService.sendSurveySelectingItems({
        status: EResearchNps.ANSWERED,
        rating: rating[0],
        surveyText: npsText
      })
        .then(() => {
          setSurveySent(true);
          dispatch(
            sendTagging({
              screenName: 'pedido_finalizado',
              modal_name: 'pesquisa_de_satisfacao_gerdau_mais',
              event: 'feedback_modal_enviado',
              tipo_compra
            })
          );
        })
        .catch((err) => {
          console.error({ err });
          setLoading(false);
          dispatch(
            sendTagging({
              event: 'error',
              screenName: commerceScreenName,
              mensagem:
                'Falha ao enviar a resposta. Por favor, tente novamente.'
            })
          );
          dispatch(
            addToast({
              type: EToastTypes.DANGER,
              message:
                'Falha ao enviar a resposta. Por favor, tente novamente.',
              durationInMS: 5000
            })
          );
        })
        .finally(() => setLoading(false));
    }
    if (purchaseType === 'spreadsheet-upload') {
      UserService.sendSurveySheet({
        status: EResearchNps.ANSWERED,
        rating: rating[0],
        surveyText: npsText
      })
        .then(() => {
          setSurveySent(true);
          dispatch(
            sendTagging({
              screenName: 'pedido_finalizado',
              modal_name: 'pesquisa_de_satisfacao_gerdau_mais',
              event: 'feedback_modal_enviado',
              tipo_compra
            })
          );
        })
        .catch((err) => {
          console.error({ err });
          setLoading(false);
          dispatch(
            sendTagging({
              event: 'error',
              screenName: commerceScreenName,
              mensagem:
                'Falha ao enviar a resposta. Por favor, tente novamente.'
            })
          );
          dispatch(
            addToast({
              type: EToastTypes.DANGER,
              message:
                'Falha ao enviar a resposta. Por favor, tente novamente.',
              durationInMS: 5000
            })
          );
        })
        .finally(() => setLoading(false));
    }
    if (purchaseType === 'repeat-order') {
      UserService.sendSurveyHistory({
        status: EResearchNps.ANSWERED,
        rating: rating[0],
        surveyText: npsText
      })
        .then(() => {
          setSurveySent(true);
          dispatch(
            sendTagging({
              screenName: 'pedido_finalizado',
              modal_name: 'pesquisa_de_satisfacao_gerdau_mais',
              event: 'feedback_modal_enviado',
              tipo_compra
            })
          );
        })
        .catch((err) => {
          console.error({ err });
          setLoading(false);
          dispatch(
            sendTagging({
              event: 'error',
              screenName: commerceScreenName,
              mensagem:
                'Falha ao enviar a resposta. Por favor, tente novamente.'
            })
          );
          dispatch(
            addToast({
              type: EToastTypes.DANGER,
              message:
                'Falha ao enviar a resposta. Por favor, tente novamente.',
              durationInMS: 5000
            })
          );
        })
        .finally(() => setLoading(false));
    }
    setAnswered((prev) => [...prev, survey]);
  };

  const handleRating = (e: string[]) => {
    dispatch(
      sendTagging({
        screenName: 'pedido_finalizado',
        modal_name: 'pesquisa_de_satisfacao_gerdau_mais',
        nota: e[0],
        event: 'feedback_nota',
        tipo_compra
      })
    );
    setRating(e);
  };

  if (!showSurveyModal) {
    return null;
  }

  return (
    <Container>
      {!surveySent && (
        <Modal
          title="Pesquisa de satisfação da Gerdau mais"
          buttons={{
            left: {
              name: 'Avaliar depois',
              children: 'Avaliar depois',
              onClick: () => onAnswerLaterClick(),
              loading
            },
            right: {
              name: 'Avaliar experiência',
              children: 'Avaliar experiência',
              onClick: () => onSendClick(),
              loading,
              disabled
            }
          }}
          onClose={() => onDontWantAnswerClick()}
          modalSize="sm"
        >
          <ModalContainer>
            <ModalSubtitle>* Campos obrigatórios</ModalSubtitle>
            <SurveyContainer>
              <SurveyHeader>
                <SurveyTitle>
                  Selecione uma nota de 1 a 5 para avaliar sua experiência de
                  compra na Gerdau mais:*
                </SurveyTitle>
              </SurveyHeader>

              <ChipsContainer>
                <ChipsWrapper>
                  <Chips
                    chips={plainOptions}
                    handleSelect={handleRating}
                    type="choice"
                    disabled={loading}
                    selectedChipIds={rating}
                  />
                </ChipsWrapper>

                <ChipsDescription>
                  <span>Péssima</span>
                  <span>Excelente</span>
                </ChipsDescription>
              </ChipsContainer>
            </SurveyContainer>

            <SurveyContainer>
              <TextArea
                label="Compartilhe mais detalhes"
                name="nps-textarea"
                about="nps-textarea-comment"
                resizable={false}
                supportMessage="Comente como você selecionou produtos e fez um pedido"
                placeholder="Avaliei com esta nota porque minha experiência foi..."
                maxLength={500}
                cols={70}
                value={npsText}
                disabled={loading}
                onChange={(e) => setNpsText(e.target.value)}
                style={{ width: '100%' }}
              />
            </SurveyContainer>
          </ModalContainer>
        </Modal>
      )}

      {surveySent && (
        <Modal
          title="Pesquisa de satisfação da Gerdau mais"
          buttons={{
            right: {
              name: 'Fechar',
              children: 'Fechar',
              onClick: () => {
                setShowSurveyModal(false);
                setSurveySent(false);
              }
            }
          }}
          onClose={() => {
            setShowSurveyModal(false);
            setSurveySent(false);
          }}
          modalSize="sm"
        >
          <SurveySuccess>
            <span>Suas respostas foram enviadas.</span>
            <span>Agradecemos pela participação!</span>
          </SurveySuccess>
        </Modal>
      )}
    </Container>
  );
};
